<template>
    <span
        v-if="!editable && (state?.linked || file.expiryError)"
        class="text-muval-gray-2 text-xs"
        >{{ file.expiryError ? 'Reminder needs attention' : statusLabel }}</span
    >
    <div
        v-else-if="editable"
        class="flex flex-col gap-3"
        @click.stop
        @touchstart.stop
    >
        <span
            v-if="state?.linked"
            class="text-muval-gray-2 text-xs"
            >{{ statusLabel }}</span
        >
        <MuvalButton
            v-if="state?.entry_id"
            type="button"
            quaternary
            small
            @click="openEntry"
            >Edit reminder</MuvalButton
        >
        <MuvalBanner
            v-if="error || file.expiryError"
            type="warning"
            >{{ error || file.expiryError }}</MuvalBanner
        >
        <MuvalButton
            v-if="canModify && expiry && !state?.linked && !editing"
            type="button"
            quinary
            small
            @click="editing = true"
            >{{ state?.needs_attention || file.expiryDraft ? 'Fix reminder' : 'Add expiry reminder' }}</MuvalButton
        >
        <template v-if="editing">
            <DocumentExpirySetup
                v-model="draft"
                :expiry="expiry"
                :title="title"
            />
            <div
                v-if="!deferSave"
                class="flex gap-2"
            >
                <MuvalButton
                    type="button"
                    :loading="saving"
                    :disabled="!draft.enabled"
                    @click="save"
                    >Save reminder</MuvalButton
                >
                <MuvalButton
                    type="button"
                    quaternary
                    @click="editing = false"
                    >Cancel</MuvalButton
                >
            </div>
        </template>
        <CompanyDiaryEntryModal
            v-if="entry"
            v-model="showEntry"
            :entry="entry"
            :can-modify="canModify"
            @saved="diarySaved"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import MuvalButton from '@buttons/MuvalButton.vue';
import MuvalBanner from '@components/MuvalBanner.vue';
import { useCompanyDiaryPermissions } from '@composables/useCompanyDiaryPermissions';
import { useUserStore } from '@store/user';
import companyDiaryApi, { type CompanyDiaryEntry } from '@/api/companyDiary';
import CompanyDiaryEntryModal from './CompanyDiaryEntryModal.vue';
import DocumentExpirySetup from './DocumentExpirySetup.vue';
import { type DocumentExpiryDraft } from './documentExpiry';
import { companyDiaryStatusLabel } from './companyDiaryStatusOptions';

const props = withDefaults(
    defineProps<{ file: any; resourceName?: string; editable?: boolean; deferSave?: boolean; expiryDate?: string }>(),
    { editable: false, deferSave: false },
);
const emit = defineEmits<{ 'expiry-changed': [date: string] }>();
async function diarySaved(saved: CompanyDiaryEntry) {
    if (saved.id === state.value?.entry_id && saved.start_datetime) {
        const date = saved.start_datetime.slice(0, 10);
        props.file.custom_properties = { ...props.file.custom_properties, expiry_date: date };
        emit('expiry-changed', date);
    }
    await refresh();
}
const expiry = computed(() => props.expiryDate ?? props.file.custom_properties?.expiry_date ?? '');
const { canModify } = useCompanyDiaryPermissions();
const user = useUserStore();
const state = computed(() => props.file.expiryDiary);
const title = computed(
    () => `${props.file.collection_name.replaceAll('_', ' ')} expires - ${props.resourceName || props.file.file_name}`,
);
const draft = ref<DocumentExpiryDraft>(
    props.file.expiryDraft || {
        enabled: true,
        title: '',
        timezone: user.getTimezone || '',
        days_before: 30,
        time: '09:00',
        recipient_user_ids: [],
        recipient_roles: [],
        replaces_file_id: null,
    },
);
const editing = ref(false);
const saving = ref(false);
const error = ref('');
const entry = ref<CompanyDiaryEntry | null>(null);
const showEntry = ref(false);
const statusLabel = computed(() =>
    state.value?.needs_attention ? 'Reminder needs attention'
    : state.value?.status !== 'TO_DO' ? `Diary entry ${companyDiaryStatusLabel(state.value?.status).toLowerCase()}`
    : state.value?.sent ? 'Reminder sent'
    : state.value?.has_reminder ? 'Reminder set'
    : 'No reminder',
);
async function refresh() {
    try {
        const { data } = await window.tenantAxios.get(`/company-diary/documents/${props.file.id}/expiry`);
        props.file.expiryDiary = data.diary;
        if (data.diary.timing && !props.file.expiryDraft)
            Object.assign(draft.value, data.diary.timing, {
                recipients_initialized: true,
                days_before: Math.max(0, data.diary.timing.days_before),
                on_date:
                    data.diary.timing.days_before < 0 ?
                        dayjs(data.diary.remind_at).tz(data.diary.timing.timezone).format('YYYY-MM-DD')
                    :   null,
                recipient_user_ids: data.diary.recipient_user_ids || [],
                recipient_roles: data.diary.recipient_roles || [],
            });
    } catch {
        error.value = 'Could not load expiry reminder status.';
    }
}
async function save() {
    saving.value = true;
    error.value = '';
    try {
        const payload: any = { ...draft.value, defer_past: true };
        const method = state.value?.linked ? 'patch' : 'post';
        const { data } = await window.tenantAxios[method](`/company-diary/documents/${props.file.id}/expiry`, payload);
        props.file.expiryDiary = data.diary;
        props.file.expiryError = '';
        props.file.expiryDraft = null;
        editing.value = false;
        return true;
    } catch (err: any) {
        error.value = err?.response?.data?.message || 'File saved, but the reminder could not be saved. Try again.';
        return false;
    } finally {
        saving.value = false;
    }
}
async function openEntry() {
    try {
        const { data } = await companyDiaryApi.showEntry(state.value.entry_id);
        entry.value = data.entry;
        showEntry.value = true;
    } catch {
        error.value = 'Could not open diary entry.';
    }
}
watch(() => [props.file.id, props.file.custom_properties?.expiry_date], refresh, { immediate: true });
watch(showEntry, (open) => {
    if (!open) void refresh();
});
defineExpose({ savePending: async () => (!editing.value || !draft.value.enabled ? true : await save()) });
</script>
